import { getShortestDatasetWithIntervalInMinutes } from 'helpers/dataset.helper/dataset.helper'
import { getChosenDateFileName } from 'helpers/export.helper'
import i18n from 'i18next'
import moment from 'moment'
import authStore from 'store/auth/auth'
import elplanStore from 'store/elplan/elplan'
import Datetime from 'utils/datetime/datetime'
import { snapshot } from 'valtio'

const DEFAULT_INTERVAL_MINUTES = 60

export async function exportPlannedElectricityProductionToExcel(
  datasets: Dataset[],
  datasetNames: Record<string, string>,
  date: { startTime: ISODateTime; endTime: ISODateTime }
): Promise<void> {
  const XLSX = await import('xlsx')
  const exportData = XLSX.utils.book_new()
  const authSnap = snapshot(authStore)
  const elplanSnap = snapshot(elplanStore)

  const title = `${authSnap.activeSystem?.display_name}`
  const subtitle = i18n.t('Total planned production')
  const year = Datetime.toLocalTime(date.startTime, 'year')
  const weekNumber = Datetime.getWeekNumber(date.startTime)
  const unit = 'MWh/h'
  const dayOfWeek = Datetime.toLocalTime(date.startTime, 'shortDayText')

  let worksheetDataInfo: (string | number | undefined)[][] = []

  worksheetDataInfo = [
    [title],
    [subtitle],
    [i18n.t('Year'), i18n.t('Week'), i18n.t('Date')],
    [year, weekNumber, Datetime.toLocalTime(date.startTime, 'onlyDate')],
    [dayOfWeek],
    [unit],
  ]

  worksheetDataInfo.push([''])

  const volumeDatasets = datasets.filter((dataset) => dataset.return_id.includes('volume'))
  const priceDatasets = datasets.filter((dataset) => dataset.return_id.includes('price'))
  const priceRows = getBidPriceRows(elplanSnap.currency ?? '', volumeDatasets, priceDatasets)
  worksheetDataInfo.push(...priceRows)

  const sortedDatasetsWithVolumes = volumeDatasets.sort(
    (a, b) => extractNumber(a.return_id) - extractNumber(b.return_id)
  )

  const datasetsWithVolumesWithoutNullValues = sortedDatasetsWithVolumes.filter((dataset) => {
    return !(Array.isArray(dataset.values) && dataset.values.every((value) => value === null))
  }) // TODO: This check can be removed when this wrike is completed: https://app-eu.wrike.com/open.htm?id=1466970642.
  // This is a temporary fix to avoid null values since hardcoded blockbids are still set in templateUiConfigs.ts.
  // When the hardcoded blockbids are removed this check can be removed and datasetsWithVolumesWithoutNullValues should be replaced with sortedDatasetsWithVolumes.

  const worksheetDatasets = getBidVolumeRows(datasetsWithVolumesWithoutNullValues, datasetNames)
  const totalWorksheet = [...worksheetDataInfo, ...worksheetDatasets]
  const addedFooter = [...totalWorksheet, [], [elplanSnap.excel_export_footer_text]]

  XLSX.utils.book_append_sheet(exportData, XLSX.utils.aoa_to_sheet(addedFooter))
  const fileName = getChosenDateFileName(`xlsx`, date.startTime, elplanSnap.excel_file_name)
  XLSX.writeFile(exportData, fileName, { sheetStubs: true })
}

function getBidPriceRows(
  currency: string,
  volumeDatasets: Dataset[],
  priceDatasets: Dataset[]
): (string | number | undefined)[][] {
  const blockBidVolumeDatasets = volumeDatasets.filter((dataset) => dataset.return_id.includes('blockbid'))
  const profileBidVolumeDatasets = volumeDatasets.filter(
    (dataset) => volumeDatasets && dataset.return_id.includes('profilebid')
  )

  const maxLength = Math.max(blockBidVolumeDatasets.length, profileBidVolumeDatasets.length)

  const blockBidPricesSortedByReturnIdNumber = priceDatasets
    .filter((dataset) => dataset.return_id.includes('blockbid'))
    .sort((a, b) => extractNumber(a.return_id) - extractNumber(b.return_id))
    .map((dataset) => dataset.values.find((value) => value !== null))

  const profileBidPricesSortedByReturnIdNumber = priceDatasets
    .filter((dataset) => dataset.return_id.includes('profilebid'))
    .sort((a, b) => extractNumber(a.return_id) - extractNumber(b.return_id))
    .map((dataset) => dataset.values.find((value) => value !== null))

  const rows: (string | number | undefined)[][] = []
  rows.push(['', `${i18n.t('Avg price')} (${currency}/MWh)`, '', '', `${i18n.t('Avg price')} (${currency}/MWh)`, ''])

  const profileBidDataInfo = getProfileBidDataInfo(profileBidVolumeDatasets)
  for (let i = 0; i < maxLength; i++) {
    rows.push([
      blockBidPricesSortedByReturnIdNumber[i] ? `${i18n.t('Block bid')} ${i + 1}` : '',
      blockBidPricesSortedByReturnIdNumber[i] ?? '',
      '',
      profileBidDataInfo[i] ?? '',
      profileBidPricesSortedByReturnIdNumber[i] ?? '',
    ])
  }

  rows.push([''])
  return rows
}

export function getBidVolumeRows(
  datasets: Dataset[],
  datasetNames: Record<string, string>
): (string | number | undefined | null)[][] {
  const worksheet: (string | number | undefined | null)[][] = [[i18n.t(`Time`)]]
  const datasetNamesInOrder: string[] = []
  const timestampToValuesByDataset: Record<string | number, Array<number | string | undefined | null>> = {}
  const { interval: smallestInterval } = getShortestDatasetWithIntervalInMinutes(datasets)
  const intervalToUse = Math.min(smallestInterval || DEFAULT_INTERVAL_MINUTES, DEFAULT_INTERVAL_MINUTES)

  datasets.forEach((dataset) => {
    const name = dataset.title ?? datasetNames[dataset.return_id]

    if (!name) {
      return
    }

    if (dataset.times.length === 0) {
      return
    }

    datasetNamesInOrder.push(name) // name can be completely without data but must be in correct order

    dataset.times.forEach((time, index) => {
      const value = dataset.values[index]
      const currentValue = value !== null && !isNaN(value) ? dataset.values[index] : null
      const intervalStart = moment(time).toISOString() as ISODateTime
      const nextTimestamp = moment(intervalStart).add(intervalToUse, 'minutes').toISOString() as ISODateTime
      const currentKey = `${moment(intervalStart).format('YYYY-MM-DD HH:mm')} - ${moment(nextTimestamp).format('HH:mm')}`

      timestampToValuesByDataset[currentKey] = timestampToValuesByDataset[currentKey] ?? [currentKey]
      timestampToValuesByDataset[currentKey].push(currentValue && isFinite(currentValue) ? currentValue : ``)
    })
  })

  worksheet[0].push(...datasetNamesInOrder)
  worksheet.push(...Object.values(timestampToValuesByDataset))

  return worksheet
}

function extractNumber(return_id: string): number {
  const match = return_id.match(/\d+/)
  return match ? parseInt(match[0], 10) : 0
}

function getProfileBidDataInfo(profileBidVolumeDatasets: Dataset[]): string[] {
  const profileBidDataInfo = []
  for (let i = 0; i < profileBidVolumeDatasets.length; i++) {
    profileBidDataInfo.push(`${i18n.t('Profile bid')} ${i + 1}`)
  }
  return profileBidDataInfo
}
